// churn-prediction.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'stats',
  tag: 'Data Science',
  stats: [
    { number: '15.6%', label: 'Avg First-Year Churn', color: 'red' },
    { number: '49%', label: '5-Year Cumulative Churn', color: 'amber' },
    { number: '80%+', label: 'Predictable w/ Data', color: 'green' },
    { number: '3-6 mo', label: 'Lead Time Possible', color: 'blue' }
  ],

  html: `
<p>Most client losses don't come out of nowhere. There are patterns, signals, and indicators that, if you know what to look for, predict which clients are likely to leave. The question is whether you're catching them in time to intervene.</p>

<p>Churn prediction isn't fortune telling. It's data analysis. And the good news is that the factors that predict Medicare churn are well-understood. Here's what the research tells us.</p>

<h2>The Science of Predicting Churn</h2>

<p>Churn prediction works by identifying factors that correlate with clients leaving. When multiple risk factors are present, the probability of churn increases. When you can score that probability for every client, you know where to focus your retention efforts.</p>

<p>Research and practical experience have identified several categories of predictive factors:</p>

<h2>Predictive Factor Categories</h2>

<h3>1. Enrollment Timing Factors</h3>

<p><strong>First-year status</strong> is the strongest single predictor of churn. Research shows 15.6% of new MA enrollees leave within year one—dramatically higher than established clients.</p>

<ul>
<li>Month 1-3: Highest risk period (coverage learning curve)</li>
<li>Month 4-12: Still elevated risk</li>
<li>Year 2+: Risk drops significantly for each retained year</li>
</ul>

<p><strong>Enrollment season also matters:</strong></p>
<ul>
<li>AEP enrollees: May not have fully evaluated options</li>
<li>SEP enrollees: Often reacting to a life event</li>
<li>OEP switchers: Already demonstrated willingness to change</li>
</ul>

<h3>2. Plan-Level Factors</h3>

<p><strong>Plan changes</strong> increase churn risk significantly:</p>
<ul>
<li>Premium increases (especially large jumps)</li>
<li>Network changes affecting client's providers</li>
<li>Formulary changes affecting client's medications</li>
<li>Benefit reductions</li>
<li>Star rating declines</li>
</ul>

<p><strong>Plan stability history:</strong></p>
<ul>
<li>Plans with volatile year-over-year changes = higher churn</li>
<li>Plans with consistent benefits and networks = lower churn</li>
</ul>

<h3>3. Engagement Factors</h3>

<p><strong>Contact recency</strong> is highly predictive:</p>
<ul>
<li>Clients contacted in last 90 days: Lower risk</li>
<li>No contact in 6+ months: Elevated risk</li>
<li>No contact in 12+ months: High risk</li>
</ul>

<p><strong>Response patterns matter:</strong></p>
<ul>
<li>Returning calls promptly: Lower risk</li>
<li>Ignoring outreach attempts: Higher risk</li>
<li>Sudden silence after regular contact: Warning sign</li>
</ul>

<h3>4. Service History Factors</h3>

<p><strong>Issue history</strong> affects churn probability:</p>
<ul>
<li>Clients with unresolved issues: High risk</li>
<li>Clients with resolved issues: Can be lower risk (proved you help)</li>
<li>Multiple issues in short period: Elevated risk</li>
</ul>

<p><strong>Types of issues that predict churn:</strong></p>
<ul>
<li>Coverage denials</li>
<li>Pharmacy problems</li>
<li>Provider access issues</li>
<li>Billing confusion</li>
<li>Premium/cost complaints</li>
</ul>

<h3>5. Population Factors</h3>

<p><strong>Demographic and coverage characteristics:</strong></p>
<ul>
<li>Dual eligibles: Higher baseline churn (SEP flexibility)</li>
<li>SNP members: Varies by SNP type</li>
<li>High medical utilization: More plan friction opportunities</li>
<li>Multiple chronic conditions: More medication/network sensitivity</li>
</ul>

<h3>6. External Factors</h3>

<p><strong>Market and timing context:</strong></p>
<ul>
<li>Pre-AEP period: Elevated risk (shopping season)</li>
<li>Competitor marketing intensity</li>
<li>Life events (spouse death, move, etc.)</li>
<li>Financial stress signals</li>
</ul>

<h2>Combining Factors Into Risk Scores</h2>

<p>The power of churn prediction comes from combining multiple factors. A single factor (like first-year status) elevates risk. Multiple factors together (first-year + plan change + no recent contact) indicate high risk.</p>

<p><strong>Example risk calculation:</strong></p>
<ul>
<li>Base risk: 10% (average annual churn)</li>
<li>First-year client: +15%</li>
<li>Plan has significant network change: +10%</li>
<li>No contact in 6+ months: +10%</li>
<li>Previous unresolved issue: +10%</li>
<li>Combined risk: 55% (high priority)</li>
</ul>

<p>This client should be at the top of your outreach list.</p>

<h2>Lead Time: When Can You Predict?</h2>

<p>Many churn factors are visible weeks or months before the client actually leaves:</p>

<ul>
<li><strong>3-6 months ahead:</strong> Plan changes announced (ANOC), engagement drop-off begins</li>
<li><strong>1-3 months ahead:</strong> Multiple warning signs accumulating</li>
<li><strong>1-4 weeks ahead:</strong> Often too late for effective intervention</li>
</ul>

<p>The goal is catching risk signals in the 3-6 month window when intervention is most effective.</p>

<h2>From Prediction to Action</h2>

<p>A risk score is only useful if it drives action:</p>

<h3>High Risk (75%+ probability)</h3>
<ul>
<li>Immediate personal outreach</li>
<li>Priority scheduling for review</li>
<li>Address specific risk factors directly</li>
</ul>

<h3>Elevated Risk (50-75%)</h3>
<ul>
<li>Scheduled outreach within 2 weeks</li>
<li>Proactive problem-solving</li>
<li>Enhanced touchpoint frequency</li>
</ul>

<h3>Moderate Risk (25-50%)</h3>
<ul>
<li>Regular check-in cadence</li>
<li>Monitor for escalation</li>
<li>Standard retention workflows</li>
</ul>

<h3>Low Risk (Under 25%)</h3>
<ul>
<li>Standard touchpoint schedule</li>
<li>Annual review</li>
<li>Ongoing relationship maintenance</li>
</ul>

<h2>Implementing Prediction Systems</h2>

<p>You can implement churn prediction at different sophistication levels:</p>

<h3>Basic: Manual Risk Assessment</h3>
<ul>
<li>Review client list quarterly</li>
<li>Flag clients with obvious risk factors</li>
<li>Prioritize outreach manually</li>
</ul>

<h3>Intermediate: Spreadsheet-Based Scoring</h3>
<ul>
<li>Track risk factors in CRM</li>
<li>Calculate simple risk scores</li>
<li>Sort/filter by score for prioritization</li>
</ul>

<h3>Advanced: Automated Risk Scoring</h3>
<ul>
<li>System calculates scores automatically</li>
<li>Real-time updates as factors change</li>
<li>Alerts when scores cross thresholds</li>
<li>Machine learning optimization over time</li>
</ul>

<p>The advanced approach is what Pro IRP provides—but even basic manual assessment is better than no prediction at all.</p>

<h2>Measuring Prediction Accuracy</h2>

<p>Track how well your predictions match actual outcomes:</p>

<ul>
<li><strong>True positives:</strong> High-risk clients who did leave (or were saved)</li>
<li><strong>False positives:</strong> High-risk clients who stayed without intervention</li>
<li><strong>True negatives:</strong> Low-risk clients who stayed</li>
<li><strong>False negatives:</strong> Low-risk clients who left unexpectedly</li>
</ul>

<p>Use false negatives to identify risk factors you're missing. Use false positives to refine over-prediction.</p>
  `,

  faqs: [
    {
      question: "What factors best predict Medicare client churn?",
      answer: "Strongest predictors: first-year status, plan changes affecting the client, contact recency, unresolved service issues, and population type (dual eligibles churn more). Combining multiple factors creates more accurate predictions than any single factor."
    },
    {
      question: "How far in advance can you predict churn?",
      answer: "Many factors are visible 3-6 months ahead: plan changes announced in ANOC, engagement drop-off patterns, accumulating warning signs. The goal is intervention in this window when it's most effective—waiting until 1-4 weeks before departure is often too late."
    },
    {
      question: "How do risk scores work?",
      answer: "Risk scores combine multiple factors weighted by their predictive power. Example: base risk 10% + first-year (+15%) + plan change (+10%) + no recent contact (+10%) = 45% risk. Higher scores mean higher probability of churn—prioritize accordingly."
    },
    {
      question: "What should I do when a client has a high risk score?",
      answer: "Immediate personal outreach to address specific risk factors. Don't wait for scheduled touchpoints. High-risk clients (75%+) need intervention now. The score tells you who—the risk factors tell you what to talk about."
    },
    {
      question: "Can I implement churn prediction without sophisticated tools?",
      answer: "Yes, but at different effectiveness levels. Basic: quarterly manual review of risk factors. Intermediate: spreadsheet tracking with calculated scores. Advanced: automated scoring with real-time alerts (what Pro IRP provides). Even basic is better than nothing."
    }
  ],

  sources: [
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    },
    {
      title: "Medicare Advantage Disenrollment Patterns",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10457718/",
      publisher: "JAMA Health Forum/PMC",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Prediction is only useful if you act on it. Pro IRP automatically calculates risk scores for every client, alerts you when scores cross thresholds, and tells you exactly which factors are driving the risk—so you know who to call and what to discuss.`
};

export default content;
